"use client";

import {
  ChatBubbleBottomCenterIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  MapPinIcon,
} from "@heroicons/react/24/solid";
// Only import motion from framer-motion, not the entire library
import { motion } from "framer-motion";
import { InputHTMLAttributes, ReactNode, useRef } from "react";
import { SITE_URL } from "@/lib/env";

export default function Contact() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Davis Construction Group",
            description:
              "Certified General Contractor in South Florida specializing in residential, commercial, and industrial construction, including septic systems, propane installations, plumbing, and remodeling.",
            url: SITE_URL,
            telephone: "(954) 444-6797",
            email: "office@constructiondavis.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "9116 Griffin Rd, Suite A",
              addressLocality: "Cooper City",
              addressRegion: "FL",
              postalCode: "33328",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 26.063314,
              longitude: -80.276527,
            },
            areaServed: [
              {
                "@type": "City",
                name: "Cooper City",
              },
              {
                "@type": "AdministrativeArea",
                name: "Broward County",
              },
              {
                "@type": "AdministrativeArea",
                name: "Miami-Dade County",
              },
              {
                "@type": "AdministrativeArea",
                name: "Palm Beach County",
              },
            ],
            openingHoursSpecification: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
              ],
              opens: "08:00",
              closes: "17:00",
            },
            priceRange: "$$",
            sameAs: [
              "https://www.facebook.com/davisconstructiongroup",
              // Add more social links if available
            ],
          }),
        }}
      />
      <main className="overflow-x-clip">
        <section className="min-h-[94vh] px-10 relative house-background flex flex-col gap-5 justify-center">
          <div className="w-full max-w-screen-2xl mx-auto z-10 space-y-5">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="pb-2 bg-gradient-to-r from-white to-amber-500 bg-clip-text text-transparent max-w-5xl font-bold text-4xl md:text-6xl"
            >
              Contact Davis Construction Group
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="max-w-xl text-white/90 text-sm md:text-lg font-light"
            >
              With over 50 years of experience serving South Florida, Davis Construction Group is your trusted partner for septic systems, propane installations, plumbing, and general contracting. From new builds to emergency repairs, we&#39;re here to help bring your vision to life. Get in touch for a free consultation today.
            </motion.p>
          </div>
          <div className="absolute inset-0 backdrop-blur-sm" />
          <div className="absolute w-full h-16 bottom-0 bg-gradient-to-t from-background to-yellow-500" />
        </section>
        <div className="p-5 my-10 h-full">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Form */}
              <section aria-labelledby="contact-heading">
                <h2 id="contact-heading" className="text-3xl font-bold text-gray-900 mb-6">
                  Send Us a Message
                </h2>
                <p className="text-gray-600 mb-6">
                  Ready to discuss your project? Fill out the form below, and we&#39;ll respond within 24 hours. Whether it&#39;s a septic installation, propane setup, or full remodel, our team is equipped to handle it.
                </p>
                <form 
                  className="space-y-6"
                  onSubmit={(e) => {
                    e.preventDefault();
                    try {
                      // Form submission handling - prevent default to avoid console errors
                      // In production, this would submit to an API endpoint
                      const formData = new FormData(e.currentTarget);
                      // Silently handle form submission
                      // Note: Actual form submission logic should be implemented
                    } catch (error) {
                      // Silently handle form submission errors
                      if (process.env.NODE_ENV === 'development') {
                        // Only log in development
                      }
                    }
                  }}
                >
                  <BaseInput
                    label="Full Name"
                    placeholder="Enter your full name"
                    type="text"
                    icon={<UserIcon width={20} height={20} />}
                    required
                  />
                  <BaseInput
                    label="Email Address"
                    placeholder="Enter your email"
                    icon={<EnvelopeIcon width={20} height={20} />}
                    type="email"
                    required
                  />
                  <BaseInput
                    label="Phone Number"
                    placeholder="Enter your phone"
                    type="tel"
                    icon={<PhoneIcon width={20} height={20} />}
                    required
                  />
                  <BaseInput
                    label="Message"
                    placeholder="Describe your project or inquiry"
                    type="textarea"
                    icon={<ChatBubbleBottomCenterIcon width={20} height={20} />}
                    rows={4}
                    required
                  />
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    className="w-full bg-amber-500 text-white py-3 px-6 rounded-md font-semibold hover:bg-amber-600 transition-colors"
                  >
                    Request Free Consultation
                  </motion.button>
                </form>
              </section>

              {/* NAP and Map */}
              <section aria-labelledby="location-heading">
                <h2 id="location-heading" className="text-3xl font-bold text-gray-900 mb-6">
                  Visit Us
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-3">
                    <MapPinIcon className="w-6 h-6 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Davis Construction Group</p>
                      <p className="text-gray-600">
                        9116 Griffin Rd, Suite A<br />
                        Cooper City, FL 33328
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <PhoneIcon className="w-6 h-6 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">(954) 444-6797</p>
                      <p className="text-gray-600">Available Monday - Friday, 8 AM - 5 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <EnvelopeIcon className="w-6 h-6 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">office@constructiondavis.com</p>
                      <p className="text-gray-600">For inquiries and quotes</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3462.7536185012905!2d-80.27652692473528!3d26.063314977163618!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9a875ad4c977f%3A0xaec988604d05a780!2s9116%20Griffin%20Rd%2C%20Cooper%20City%2C%20FL%2033328%2C%20USA!5e0!3m2!1sen!2sus!4v1725124752369!5m2!1sen!2sus"
                    className="w-full h-80 rounded-xl border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Davis Construction Group Location"
                    allowFullScreen
                    aria-label="Interactive map showing Davis Construction Group location"
                  />
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

interface BaseInputProps extends InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  label?: string;
  icon?: ReactNode;
  type?: string;
  rows?: number;
}

function BaseInput({ label, icon, rows, ...props }: BaseInputProps) {
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const isTextarea = props.type === 'textarea';

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div
        onClick={(event) => {
          event.stopPropagation();
          inputRef.current?.focus();
        }}
        className="flex items-start gap-3 border border-gray-300 rounded-md bg-white p-3 focus-within:border-amber-500 transition-colors"
      >
        {icon && <div className="flex-shrink-0 mt-1">{icon}</div>}
        {isTextarea ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            rows={rows}
            {...props}
            className="flex-1 bg-transparent border-none focus:outline-none resize-none text-gray-900 placeholder-gray-500"
          />
        ) : (
          <input
            ref={inputRef}
            {...props}
            className="flex-1 bg-transparent border-none focus:outline-none text-gray-900 placeholder-gray-500"
          />
        )}
      </div>
    </div>
  );
}